import React from "react";
import { Link } from "react-router-dom";
import LegalPage, {
	Bullets,
	Note,
	Section,
	SubSection,
} from "../components/legal/LegalPage";

/**
 * Public privacy policy, from the client's approved copy. Both app stores
 * require a reachable URL for this, and the Play Data Safety / Apple App
 * Privacy answers must match what it says (#157).
 */
const Privacy = () => (
	<LegalPage
		title="Privacy Policy"
		updated="3 September 2026"
		toc
		intro={
			<>
				<p className="mb-3">
					WholesaleDai ("WholesaleDai", "we", "us" or "our") operates the
					WholesaleDai website, mobile application and related services
					(together, the <span className="font-semibold">Platform</span>).
				</p>
				<p className="mb-3">
					We are committed to protecting the privacy and security of our users.
					This Privacy Policy explains how we collect, use, store, process and
					protect personal and business information when you access or use the
					Platform.
				</p>
				<p>
					We handle personal information in accordance with the{" "}
					<span className="font-semibold">
						Individual Privacy Act, 2075 (2018)
					</span>{" "}
					and the Individual Privacy Regulation, 2077 (2020), the{" "}
					<span className="font-semibold">
						Electronic Transactions Act, 2063 (2008)
					</span>{" "}
					and the{" "}
					<span className="font-semibold">
						Electronic Commerce Act, 2081 (2025)
					</span>
					. By using WholesaleDai you consent to the collection and use of
					information as described in this Policy.
				</p>
			</>
		}
	>
		<Section heading="1. Information we collect">
			<p className="mb-5">
				We collect personal, business, transactional and technical information
				from users of the Platform.
			</p>

			<SubSection heading="1.1 Personal information">
				<p className="mb-3">
					When you register or use the Platform we collect:
				</p>
				<Bullets
					items={[
						"Full name",
						"Mobile phone number — this is also your login identifier",
						"Email address",
						"Business address and delivery address",
						"A profile photo, if you choose to upload one",
						"Identification information, where required for verification",
						"Any other information you provide voluntarily",
					]}
				/>
			</SubSection>

			<SubSection heading="1.2 Business information">
				<p className="mb-3">
					Because WholesaleDai is a business-to-business (B2B) marketplace, we
					collect business information, including:
				</p>
				<Bullets
					items={[
						"Business or company name",
						"Business registration details",
						"PAN or VAT number",
						"Business registration certificates",
						"Business address and nature of business",
						"Authorised representative details",
						"Other documents required for verification",
					]}
				/>
			</SubSection>

			<SubSection heading="1.3 Transaction information">
				<p className="mb-3">
					When you place, receive, sell or fulfil orders through the Platform
					we collect information relating to:
				</p>
				<Bullets
					items={[
						"Products ordered or sold, and order quantities",
						"Wholesale prices and Minimum Order Quantities (MOQs)",
						"Payment method and payment status",
						"Transaction history and delivery details",
						"Refund and return requests",
						"Communications relating to orders",
					]}
				/>
			</SubSection>

			<SubSection heading="1.4 Device and technical information">
				<p className="mb-3">
					When you access the Platform we automatically collect certain
					technical information, including:
				</p>
				<Bullets
					items={[
						"IP address and the time of each sign-in, kept as a security record",
						"A push notification token for each device you sign in on, so we can send you order and account updates",
						"Standard server log data created when you use the Platform, including your IP address, browser type and the pages or endpoints you request",
					]}
				/>
			</SubSection>

			<SubSection heading="1.5 Communications">
				<p className="mb-3">
					We collect information contained in communications between you and
					WholesaleDai, including customer support requests, emails, phone
					communications, live chat messages, feedback, complaints, reviews and
					messages submitted through the Platform.
				</p>
			</SubSection>
		</Section>

		<Section heading="2. How we use your information">
			<p className="mb-3">We use the information we collect to:</p>
			<Bullets
				items={[
					"Create, secure and manage user accounts, and sign you in",
					"Verify Buyers and Sellers, and verify business credentials and documents",
					"Process and manage orders, and facilitate B2B transactions",
					"Process payments and coordinate delivery and logistics",
					"Provide customer support and handle complaints",
					"Send you service-related notifications about your account, orders and Platform announcements",
					"Improve the Platform, our services, security and operational processes",
					"Prevent fraud and unauthorised activity, and detect security incidents",
					"Enforce our Terms and Conditions",
					"Comply with applicable legal, tax and regulatory requirements",
					"Conduct analytics and business research on an aggregated basis",
				]}
			/>
			<Note>
				<p>
					We use your information only for the purposes for which it was
					collected, as required by the Individual Privacy Act, 2075. We do
					not sell your personal or business information, and we do not use it
					for third-party advertising.
				</p>
			</Note>
		</Section>

		<Section heading="3. Business verification">
			<p className="mb-3">
				WholesaleDai requires Buyers and Sellers to submit business verification
				documents. These may include PAN certificates, VAT certificates, company
				registration certificates, business licences, identification documents,
				authorisation documents and other documents required by applicable law.
			</p>
			<p className="mb-3">
				We use these documents to verify the authenticity and legitimacy of
				businesses operating on the Platform. Access to uploaded verification
				documents is restricted to staff who need it to review an application.
			</p>
			<p>
				We reserve the right to approve, reject, suspend or terminate accounts
				based on the results of our verification process. Once an account is
				approved, its PAN number and verification documents are locked and can
				only be changed by our staff.
			</p>
		</Section>

		<Section heading="4. How we share information">
			<p className="mb-5">
				We share your information only with the parties needed to operate the
				Platform and provide our services, and only as far as each one needs.
				Except as described below, we do not disclose your personal information
				to third parties without your consent.
			</p>

			<SubSection heading="4.1 Sellers and Buyers">
				<p className="mb-3">
					Information is shared between Buyers and Sellers to facilitate
					commercial transactions. This may include business name, contact
					information, delivery information, order details and product
					information. Only information reasonably necessary to complete the
					transaction is shared.
				</p>
			</SubSection>

			<SubSection heading="4.2 Payment service providers">
				<p>
					Payments may be processed through authorised third-party payment
					providers, including digital wallets, banks and payment gateways
					licensed by Nepal Rastra Bank. We share the transaction information
					necessary to process a payment.
				</p>
			</SubSection>

			<SubSection heading="4.3 Logistics and delivery partners">
				<p>
					We share the information needed to deliver an order — recipient name,
					phone number, delivery address and order details — with logistics
					providers, delivery partners, Sellers and other service providers
					responsible for delivering products.
				</p>
			</SubSection>

			<SubSection heading="4.4 Service providers">
				<p className="mb-3">
					We engage third-party service providers to help operate the Platform.
					The providers that handle user data are:
				</p>
				<Bullets
					items={[
						"Google Firebase (Cloud Messaging) — delivers push notifications to your device.",
						"Tawk.to — powers the in-app live chat. If you open a chat, your name, email address and phone number are passed to it so our support agents know who they are speaking to.",
						"Cloud hosting and storage providers — host the Platform, its database and uploaded documents.",
					]}
				/>
				<p className="mt-3">
					These providers may access information only as necessary to provide
					services on our behalf, and are required to keep it confidential.
				</p>
			</SubSection>

			<SubSection heading="4.5 Legal requirements">
				<p className="mb-3">We may disclose information where necessary to:</p>
				<Bullets
					items={[
						"Comply with applicable laws",
						"Respond to a lawful order of a court or a request from a government authority in Nepal",
						"Establish, exercise or defend a legal claim",
						"Protect WholesaleDai, our users or the public",
						"Prevent fraud or security threats",
					]}
				/>
			</SubSection>

			<SubSection heading="4.6 Storage outside Nepal">
				<p>
					Some of our hosting and service providers store or process data on
					servers located outside Nepal. Where this happens we take reasonable
					steps to ensure the information continues to be protected to the
					standard described in this Policy.
				</p>
			</SubSection>
		</Section>

		<Section heading="5. Payment information">
			<p className="mb-3">
				The Platform supports payment methods including digital wallets, bank
				transfers, Cash on Delivery (COD) where available, and third-party
				payment gateways.
			</p>
			<p className="mb-3">
				Electronic payments are processed by authorised third-party payment
				service providers. WholesaleDai does not store sensitive payment
				credentials such as full card numbers, PINs or wallet passwords.
			</p>
			<p>
				We encourage you to review the privacy policies and terms of the payment
				provider you choose.
			</p>
		</Section>

		<Section heading="6. Data storage and security">
			<p className="mb-3">
				We take reasonable administrative, technical and organisational measures
				to protect information against unauthorised access, unauthorised
				disclosure, loss, misuse, alteration and destruction. Traffic between
				our apps and our servers is encrypted in transit, and passwords are
				stored hashed — never in plain text.
			</p>
			<p className="mb-3">
				However, no electronic system, internet transmission or storage method is
				completely secure. While we make reasonable efforts to protect your
				information, we cannot guarantee absolute security.
			</p>
			<p className="mb-3">
				You are responsible for protecting your own account credentials —
				passwords, OTPs, login credentials and the devices you use to access the
				Platform. Never share your password or an OTP with anyone, including
				anyone claiming to be from WholesaleDai. We will never ask you for your
				password or OTP.
			</p>
			<p>
				If you believe your account has been accessed without your permission,
				contact us immediately using the details in section 15.
			</p>
		</Section>

		<Section heading="7. Data retention">
			<p className="mb-3">
				We keep your account information for as long as your account is open, and
				afterwards only as long as reasonably necessary to complete transactions,
				resolve disputes, prevent fraud, enforce our agreements and meet our
				legal obligations.
			</p>
			<p className="mb-3">
				Records of completed transactions, invoices and accounts are retained for
				the periods required by Nepali tax law — currently five years under the{" "}
				<span className="font-semibold">Income Tax Act, 2058 (2002)</span> and
				six years for VAT records under the{" "}
				<span className="font-semibold">Value Added Tax Act, 2052 (1996)</span>.
				After your account is deleted these records are no longer linked to your
				name, phone number, email address or documents.
			</p>
			<p>
				When information is no longer required, we delete it, anonymise it, or
				continue to hold it securely only where the law requires us to.
			</p>
		</Section>

		<Section heading="8. Cookies and similar technologies">
			<p className="mb-3">
				Our website uses cookies and similar technologies to maintain your
				session, remember your preferences, analyse Platform usage, improve
				performance and enhance security. Our mobile application does not use
				cookies; it stores an equivalent session token on your device.
			</p>
			<p>
				You can control cookies through your browser settings. Disabling them may
				affect some features of the website — in particular, staying signed in.
			</p>
		</Section>

		<Section heading="9. Third-party services">
			<p className="mb-3">
				The Platform may link to or integrate services provided by third parties,
				including payment providers, logistics companies, digital wallet
				providers, analytics services and other technology providers.
			</p>
			<p>
				WholesaleDai is not responsible for the privacy practices of third-party
				websites or services. We encourage you to review their privacy policies
				before using them.
			</p>
		</Section>

		<Section heading="10. Your responsibilities">
			<p className="mb-3">
				You must ensure that the information you provide to WholesaleDai is
				accurate, complete, current and lawfully obtained, and you must update
				your account information when it changes.
			</p>
			<p>
				You must not provide false, misleading or fraudulent information, and you
				must not upload documents belonging to another person or business without
				their authority.
			</p>
		</Section>

		<Section heading="11. Children's privacy">
			<p className="mb-3">
				WholesaleDai is a B2B Platform intended for commercial and business use.
				The Platform is not intended for anyone under the age of 18, which is the
				age of majority under the National Civil Code, 2074 (2017).
			</p>
			<p>
				We do not knowingly collect personal information from children. If we
				become aware that we hold information about a person who is not legally
				eligible to use the Platform, we will delete that information and
				terminate the account.
			</p>
		</Section>

		<Section heading="12. Your rights">
			<p className="mb-3">
				Under the Individual Privacy Act, 2075 (2018) and subject to other
				applicable laws, you have the right to:
			</p>
			<Bullets
				items={[
					"Request access to the personal information we hold about you",
					"Request correction of inaccurate or incomplete information",
					"Request deletion of your information where the law allows it",
					"Withdraw your consent to processing that relies on consent",
					"Raise a concern or complaint about how we process your information",
				]}
			/>
			<p className="mt-3">
				Most of your details can be corrected yourself under{" "}
				<span className="font-semibold">Profile → Edit Profile</span>. For
				anything else, contact us using the details in section 15. We will
				respond to your request within 15 days.
			</p>
			<p className="mt-3">
				Some information must be retained for legal, regulatory, accounting,
				fraud-prevention or operational reasons even after you ask us to delete
				it. Where that applies we will tell you which information we are keeping
				and why.
			</p>
		</Section>

		<Section heading="13. Deleting your account">
			<p className="mb-3">
				You can delete your account at any time from{" "}
				<span className="font-semibold">Profile → Delete Account</span> in the
				mobile app. Deleting erases your name, phone number, email address, PAN
				number and uploaded documents, signs you out of every device and prevents
				any further sign-in.
			</p>
			<p>
				If you cannot sign in — for example because your account is still
				awaiting verification — use our{" "}
				<Link
					to="/delete-account"
					className="text-brand-600 font-semibold underline"
				>
					account deletion request page
				</Link>{" "}
				instead. Transaction records we are required to keep under tax law are
				retained as described in section 7.
			</p>
		</Section>

		<Section heading="14. Changes to this Privacy Policy">
			<p className="mb-3">
				We may update or modify this Privacy Policy from time to time. Any
				updated version will be posted at{" "}
				<a
					href="https://www.wholesaledai.com"
					className="text-brand-600 font-semibold underline"
				>
					www.wholesaledai.com
				</a>{" "}
				and within the WholesaleDai application, with the date at the top of this
				page updated.
			</p>
			<p>
				Where a change is significant we will notify you in the app or by email
				before it takes effect, and where the law requires it we will ask for
				your consent again. We encourage you to review this Policy periodically.
			</p>
		</Section>

		<Section heading="15. Contact and complaints">
			<p className="mb-3">
				If you have questions, concerns, requests or complaints about this
				Privacy Policy or how we handle your information, please contact us. We
				register every complaint on receipt and aim to resolve it within 15 days,
				as required by the Electronic Commerce Act, 2081.
			</p>
			<Bullets
				items={[
					"Company name: WholesaleDai",
					"Office address: O2, Newroad, Kathmandu-22, Nepal",
					"Phone: +977-1-5927066",
					"Email: info@wholesaledai.com",
					"Alternative email: infowholesaledai@gmail.com",
					"Website: www.wholesaledai.com",
				]}
			/>
			<p className="mt-3">
				You can also reach us through the{" "}
				<Link
					to="/contact-us"
					className="text-brand-600 font-semibold underline"
				>
					contact page
				</Link>{" "}
				or the live chat on the Platform. See also our{" "}
				<Link to="/terms" className="text-brand-600 font-semibold underline">
					Terms and Conditions
				</Link>
				.
			</p>
		</Section>
	</LegalPage>
);

export default Privacy;
